import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Send, ArrowLeft, Trash2 } from "lucide-react";
import { api, mediaUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShareButton } from "../components/ShareButton";

const PostDetail = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(
    () => api.get(`/posts/${postId}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true)),
    [postId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const toggleLike = async () => {
    if (!user) {
      navigate("/accedi");
      return;
    }
    const { data } = await api.post(`/posts/${postId}/like`);
    setPost((p) => ({ ...p, liked_by_me: data.liked, likes_count: data.likes_count }));
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/posts/${postId}/comments`, { text: comment });
      setComment("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Ricordo eliminato.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (notFound)
    return (
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <p data-testid="post-not-found" className="font-display text-3xl italic text-[#5C534B]">Questo ricordo non è disponibile.</p>
        <Link to="/" className="mt-6 inline-block text-sm font-semibold text-[#B85328] underline underline-offset-4">
          Torna alla galleria
        </Link>
      </main>
    );
  if (!post)
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <p className="font-display text-2xl italic text-[#8C7B6E]">Caricamento...</p>
      </main>
    );

  return (
    <main className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
      <button
        data-testid="post-back-button"
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2 text-sm font-semibold text-[#8C7B6E] transition-colors hover:text-[#B85328]"
      >
        <ArrowLeft size={15} /> Indietro
      </button>

      <article data-testid="post-detail" className="overflow-hidden rounded-3xl border border-[#E8DFD5] bg-white shadow-sm">
        {post.media_type === "video" ? (
          <video src={mediaUrl(post.media_url)} controls autoPlay={false} className="max-h-[70vh] w-full bg-black object-contain" />
        ) : (
          <img src={mediaUrl(post.media_url)} alt={post.dog_name} className="max-h-[70vh] w-full object-cover" />
        )}
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <Link to={`/cani/${encodeURIComponent(post.dog_name)}`} data-testid="post-dog-diary-link">
              <h1 data-testid="post-dog-name" className="font-display text-3xl font-semibold text-[#2C2723] transition-colors hover:text-[#B85328]">
                {post.dog_name}
              </h1>
            </Link>
            <span className="text-sm text-[#8C7B6E]">condiviso da {post.author_name}</span>
          </div>
          {post.caption && <p data-testid="post-caption" className="mt-3 leading-relaxed text-[#5C534B]">{post.caption}</p>}

          <div className="mt-5 flex items-center gap-4 border-t border-[#E8DFD5] pt-5">
            <button
              data-testid="post-like-button"
              onClick={toggleLike}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                post.liked_by_me
                  ? "border-[#B85328] bg-[#B85328] text-[#FBF8F3]"
                  : "border-[#CFC0B2] text-[#5C534B] hover:border-[#B85328] hover:text-[#B85328]"
              }`}
            >
              <Heart size={16} fill={post.liked_by_me ? "#FBF8F3" : "none"} />
              {post.likes_count} {post.likes_count === 1 ? "cuoricino" : "cuoricini"}
            </button>
            <ShareButton
              path={`/ricordo/${post.post_id}`}
              title={`${post.dog_name} — Le Nuove Vite`}
              text={post.caption || `Un ricordo di ${post.dog_name}`}
              testId="post-share-button"
            />
            {(post.is_mine || user?.role === "admin") && (
              <button
                data-testid="post-delete-button"
                onClick={remove}
                className="flex items-center gap-2 rounded-full border border-[#CFC0B2] px-5 py-2.5 text-sm font-semibold text-[#8C7B6E] transition-all hover:border-[#B85328] hover:text-[#B85328]"
              >
                <Trash2 size={15} /> Elimina
              </button>
            )}
          </div>

          <section className="mt-8">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7B6E]">
              Commenti ({post.comments.length})
            </h2>
            <div data-testid="post-comments-list" className="mt-4 space-y-4">
              {post.comments.map((c) => (
                <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="rounded-2xl bg-[#F5EFE6] px-5 py-4">
                  <p className="text-xs font-semibold text-[#B85328]">{c.author_name}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#2C2723]">{c.text}</p>
                </div>
              ))}
              {post.comments.length === 0 && (
                <p className="text-sm italic text-[#8C7B6E]">Nessun commento ancora: scrivi il primo!</p>
              )}
            </div>

            {user ? (
              <form data-testid="comment-form" onSubmit={sendComment} className="mt-5 flex gap-3">
                <input
                  data-testid="comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Lascia un pensiero dolce..."
                  className="flex-1 rounded-full border border-[#E8DFD5] bg-[#FBF8F3] px-5 py-3 text-sm text-[#2C2723] outline-none transition-all placeholder:text-[#8C7B6E]/60 focus:border-[#B85328] focus:ring-2 focus:ring-[#B85328]/15"
                />
                <button
                  type="submit"
                  data-testid="comment-submit-button"
                  disabled={busy}
                  className="flex items-center gap-2 rounded-full bg-[#1F1A17] px-6 py-3 text-sm font-semibold text-[#FBF8F3] transition-all hover:bg-[#B85328] disabled:opacity-60"
                >
                  <Send size={14} />
                </button>
              </form>
            ) : (
              <p className="mt-5 text-sm text-[#8C7B6E]">
                <Link to="/accedi" data-testid="post-login-to-comment" className="font-semibold text-[#B85328] underline underline-offset-4">
                  Accedi
                </Link>{" "}
                per lasciare un commento o un cuoricino.
              </p>
            )}
          </section>
        </div>
      </article>
    </main>
  );
};

export default PostDetail;
